import React from 'react';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import type { DashboardOverviewAnalyticsResponse } from '@/lib/api/dashboard';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import dashboardRecentPaymentsIcon from '@/assets/images/dashboard-recent-payments.svg';

import { CS_PRIMARY } from './dashboardHomeTokens';
import {
  formatDashboardCurrency,
  formatDashboardPaidDate,
} from './dashboardHomeFormatters';

const RecentPaymentProfileIcon = () => (
  <Box
    component="svg"
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    sx={{ display: 'block' }}
  >
    <circle cx="7.71401" cy="3.85707" r="2.57143" fill="#000" />
    <ellipse opacity="0.5" cx="7.71484" cy="10.9284" rx="4.5" ry="2.57143" fill="#000" />
  </Box>
);

export interface DashboardAnalyticsRecentPaymentsCardProps {
  data?: DashboardOverviewAnalyticsResponse;
  isLoading?: boolean;
  onViewAll?: () => void;
}

export const DashboardAnalyticsRecentPaymentsCard: React.FC<DashboardAnalyticsRecentPaymentsCardProps> = ({
  data,
  isLoading = false,
  onViewAll,
}) => {
  const rows = data?.recentPayments.items ?? [];
  const isEmpty = !isLoading && rows.length === 0;
  const revenueCurrency = data?.cards.monthlyRevenue.currency;
  const isCustomRange = Boolean(data?.period.isCustomRange);
  const customPeriodLabel = data?.period.startDate
    ? data.period.endDate && data.period.endDate !== data.period.startDate
      ? `${formatDashboardPaidDate(data.period.startDate)} - ${formatDashboardPaidDate(data.period.endDate)}`
      : formatDashboardPaidDate(data.period.startDate)
    : null;
  const periodLabel = isCustomRange
    ? customPeriodLabel
    : data?.period.monthStart
      ? formatDashboardPaidDate(data.period.monthStart)?.split(' ')[0] ?? null
      : null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #ebebeb',
        overflow: 'hidden',
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f2f2f2', minHeight: 49 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box component="img" src={dashboardRecentPaymentsIcon} alt="" aria-hidden sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>Recent Payments</Typography>
        </Stack>
        <Button
          variant="text"
          size="small"
          onClick={onViewAll}
          endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            color: CS_PRIMARY,
            minWidth: 0,
            px: 0.5,
            '&:hover': { bgcolor: 'rgba(30, 174, 219, 0.06)' },
          }}
        >
          View all
        </Button>
      </Stack>

      <Stack
        divider={rows.length > 0 ? <Divider flexItem sx={{ borderColor: '#f5f5f5' }} /> : undefined}
        sx={{
          flex: 1,
          minHeight: isEmpty ? 220 : undefined,
          ...(isEmpty
            ? {
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }
            : {}),
        }}
      >
        {isLoading && rows.length === 0 && (
          <Typography sx={{ px: 2, py: 2, fontSize: 12, color: '#4a5565' }}>Loading recent payments...</Typography>
        )}
        {isEmpty && (
          <Typography
            sx={{
              px: 2,
              py: 2,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: '#4a5565',
              lineHeight: 1.45,
            }}
          >
            No recent payments yet.
          </Typography>
        )}
        {rows.map((row) => {
          const paidDate = formatDashboardPaidDate(row.paidAt);
          const isPaid = row.status === 'paid';
          return (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 2, py: 1.5, minHeight: 64 }}
            >
              <Box
                sx={{
                  width: 27,
                  height: 27,
                  borderRadius: '7.7px',
                  bgcolor: 'rgba(226, 232, 240, 0.37)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <RecentPaymentProfileIcon />
              </Box>
              <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#1a1a1a' }}>
                  <ClientNameLink clientId={row.clientId} className="text-[#1a1a1a] hover:text-[#1a1a1a]">
                    {row.clientName}
                  </ClientNameLink>
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#000', letterSpacing: '0.01em' }}>{row.title}</Typography>
              </Stack>
              <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#111' }}>
                  {formatDashboardCurrency(row.amount, row.currency || revenueCurrency)}
                </Typography>
                {isPaid ? (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#28c76f' }}>
                    Paid{paidDate ? ` · ${paidDate}` : ''}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      bgcolor: 'rgba(255, 159, 67, 0.1)',
                      borderRadius: '4px',
                      px: 0.75,
                      py: 0.25,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#ff9f43' }}>Pending</Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ bgcolor: '#fafafa', px: 2, py: 1.25, borderTop: '1px solid #f5f5f5' }}
      >
        <Typography sx={{ fontSize: 12, color: '#000' }}>
          Total collected{periodLabel ? ` (${periodLabel})` : ''}
        </Typography>
        <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#111' }}>
          {isLoading && !data ? '--' : formatDashboardCurrency(data?.recentPayments.totalCollected, revenueCurrency)}
        </Typography>
      </Stack>
    </Paper>
  );
};
